import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';

const KEY = 'scenesList';

export const useGetScenes = (params = {}, options = {}) => {
  const { showToast } = useCustomToast();
  return useQuery(
    [KEY, params],
    async ({ queryKey }) => (await axiosInstance.get('/scenes', { params: queryKey[1] })).data,
    {
      keepPreviousData: true,
      onError: (err) => showToast('error', 'Scenes', err.response?.data?.message),
      ...options,
    }
  );
};

export const useGetScene = (id) => {
  const { showToast } = useCustomToast();
  return useQuery(
    ['scene', id],
    async () => (await axiosInstance.get(`/scenes/${id}`)).data,
    {
      enabled: !!id,
      onError: (err) => showToast('error', 'Scene', err.response?.data?.message),
    }
  );
};

export const useCreateScene = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/scenes/create', payload), {
    onSuccess: () => {
      qc.invalidateQueries(KEY);
      showToast('success', 'Scenes', 'Scene created!');
    },
    onError: (err) => showToast('error', 'Scenes', err.response?.data?.message),
  });
};

export const useUpdateScene = (id) => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.put(`/scenes/${id}/update`, payload), {
    onSuccess: () => {
      qc.invalidateQueries(KEY);
      qc.invalidateQueries(['scene', id]);
      showToast('success', 'Scenes', 'Scene updated!');
    },
    onError: (err) => showToast('error', 'Scenes', err.response?.data?.message),
  });
};

export const useDeleteScene = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/scenes/${id}/delete`), {
    onSuccess: () => {
      qc.invalidateQueries(KEY);
      showToast('success', 'Scenes', 'Scene removed!');
    },
    onError: (err) => showToast('error', 'Scenes', err.response?.data?.message),
  });
};

/**
 * Activate a scene.
 *
 * ⚠️ TWO PLAIN LINES, AND NOTHING FROM THE WIRE. The backend answers 422 with a
 * code AND a diagnostic report; showing its `message` put the sentence, the
 * code `scene_not_applied` and the scene's UUID on a café's screen at once.
 * The code selects localized copy here — headline plus what to do — and the
 * server's detail goes to the console, where it is useful.
 *
 * An unrecognised code falls back to the generic pair rather than leaking
 * whatever the server sent.
 */
export const useActivateScene = () => {
  const { showToast } = useCustomToast();
  const { t } = useTranslation();
  return useMutation((id) => axiosInstance.post(`/scenes/${id}/activate`), {
    onSuccess: () => showToast('success', 'Scenes', t('hardware.scenes.activated')),
    onError: (err) => {
      const code = err.response?.data?.error;
      const key = ['scene_empty', 'scene_not_applied'].includes(code) ? code : 'generic';
      // Diagnostics stay here, off the café's screen.
      console.warn('[scenes] activation refused', {
        code, message: err.response?.data?.message, data: err.response?.data?.data,
      });
      showToast(
        'error',
        t(`hardware.scenes.errors.${key}.title`),
        t(`hardware.scenes.errors.${key}.body`),
      );
    },
  });
};
